import { DatePicker, TimePicker, InputNumber, Input, Select } from "antd";
import dayjs from "dayjs";
import toLower from "lodash/toLower";
import find from "lodash/find";
import get from "lodash/get";
import includes from "lodash/includes";
import map from "lodash/map";
import * as XLSX from "xlsx";

export const getRequiredHeaders = (fileType, MONTHLY_HEADERS, DAILY_HEADERS) => {
    return fileType === "monthly" ? MONTHLY_HEADERS : DAILY_HEADERS;
};

export const validateExcelData = (headers, data, requiredHeaders) => {
    const errors = [];
    const rowErrorMap = {};

    const missingHeaders = requiredHeaders.filter((h) => !headers.includes(h));
    if (missingHeaders.length) {
        errors.push({
            key: "header-error",
            context: "Header Row",
            message: `Missing column(s): ${missingHeaders.join(", ")}`,
            isHeaderError: true,
        });
        return { errors, rowErrorMap };
    }

    data.forEach((row, index) => {
        const missingFields = requiredHeaders.filter(
            (h) => !row[h] || String(row[h]).trim() === ""
        );

        if (missingFields.length > 0) {
            errors.push({
                key: index,
                context: `Row ${index + 2}`,
                message: `Missing data in: ${missingFields.join(", ")}`,
                isHeaderError: false,
            });
            rowErrorMap[row.key] = missingFields;
        }
    });

    return { errors, rowErrorMap };
};


export const parseExcelFile = (file, onSuccess, onError) => {
    const reader = new FileReader();

    reader.onload = (e) => {
        try {
            const wb = XLSX.read(new Uint8Array(e.target.result), { type: "array" });
            const ws = wb.Sheets[wb.SheetNames[0]];
            const json = XLSX.utils.sheet_to_json(ws, { header: 1 });

            const filtered = json.filter(row => row.some(cell => cell !== null && cell !== undefined && cell !== ""));

            const sheetHeaders = filtered[0] || [];
            const sheetData = filtered.slice(1).map((r, i) => {
                const obj = { key: i };
                sheetHeaders.forEach((h, idx) => {
                    obj[h] = r[idx] ?? "";
                });
                return obj;
            });

            onSuccess(sheetHeaders, sheetData);
        } catch (err) {
            onError(err);
        }
    };

    reader.readAsArrayBuffer(file);
};


export const updateCellValue = (rows, rowErrors, rowKey, column, value) => {
    const updatedRows = rows.map((r) =>
        r.key === rowKey ? { ...r, [column]: value } : r
    );

    const updatedRowErrors = { ...rowErrors };
    const missing = updatedRowErrors[rowKey];

    if (missing) {
        const filtered = missing.filter((h) => h !== column);
        updatedRowErrors[rowKey] = filtered.length ? filtered : undefined;
    }

    return { updatedRows, updatedRowErrors };
};


export const excelSerialToDate = (serial) => {
    if (serial === null || serial === undefined || serial === "") {
        return serial;
    }
    if (typeof serial === 'number' && !isNaN(serial)) {
        const MS_PER_DAY = 86400 * 1000;
        const EXCEL_EPOCH_OFFSET = 25569;
        const jsTime = (serial - EXCEL_EPOCH_OFFSET) * MS_PER_DAY;
        return new Date(jsTime);
    }
    return serial;
};

export const excelTimeHandler = (value) => {
    let timeString = "";

    if (typeof value === "number") {
        timeString = excelSerialToTime(value);
    } else if (value instanceof Date) {
        timeString = dayjs(value).format("HH:mm");
    } else if (typeof value === "string") {
        const parts = value.split(":");
        if (parts.length === 2) {
            timeString = `${parts[0].padStart(2, "0")}:${parts[1].padStart(2, "0")}`;
        } else {
            timeString = value;
        }
    }
    return timeString
}
const excelSerialToTime = (serial) => {
    if (typeof serial !== "number") return "";
    const totalSeconds = Math.round(serial * 24 * 60 * 60);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`;
};

export const getInputTypeForHeader = (header) => {
    const h = header.toLowerCase();

    if (h.includes("date")) return "date";
    if (h.includes("time")) return "time";
    if (["sn", "s.n", "s_no"].some((x) => h.includes(x)))
        return "number";

    return "text";
};

export const generateStaffColumns = (headers, rowErrors, handleCellChange, config, filter) => {
    return headers.map((header) => ({
        title: header,
        dataIndex: header,
        width: 180,
        render: (_, record) => {
            const headerStr = header;
            const value = record[headerStr];
            const missing =
                rowErrors[record.key] &&
                rowErrors[record.key].includes(headerStr);
            const staff_type = find(get(config, "staff_types"), item => get(item, 'name') === value)
            const role = find(Object.values(get(config, "staff_roles")), item => get(item, 'name') === value)
            const shift = find(get(filter, "company.company_shifts"), item => get(item, 'name') === record["Shift"])
            const type = getInputTypeForHeader(header);
            if (header === "Role") {
                return (
                    <Select options={map(
                        get(config, "staff_roles"),
                        (role) => ({
                            label: get(role, "name", ""),
                            value: get(role, "value", ""),
                        })
                    )}
                        value={get(role, "value")}
                        onChange={(value) => {
                            const role = find(Object.values(get(config, "staff_roles")), item => item.value === value)
                            handleCellChange(record.key, header, get(role, "name"))
                        }
                        } style={{ width: 150 }} />
                )
            }
            if (header === "Shift") {
                return (
                    <Select options={map(
                        get(filter, "company.company_shifts"),
                        (shift) => ({
                            label: get(shift, "name", ""),
                            value: get(shift, "id", ""),
                        })
                    )}
                        placeholder="Select Shift"
                        value={get(shift, "id")}
                        onChange={(value) => {
                            const shift = find(get(filter, "company.company_shifts"), item => item.id === value)
                            handleCellChange(record.key, header, get(shift, "name"))
                        }
                        } style={{ width: 150 }}
                        className={missing ? "select-error" : ""} />
                )
            }
            if (header === "Staff Type") {
                return (
                    <Select
                        placeholder="Staff Type" options={map(
                            get(config, "staff_types"),
                            (role) => ({
                                label: get(role, "name", ""),
                                value: get(role, "value", ""),
                            })
                        )} value={get(staff_type, "value")}
                        onChange={(value) => {

                            const staff_type = find(get(config, "staff_types"), item => get(item, 'value') === value)
                            handleCellChange(record.key, header, get(staff_type, "name"))
                        }
                        } style={{ width: 150 }} />
                )
            }
            if (header === "Date") {
                const dateValue =
                    typeof value === "number"
                        ? excelSerialToDate(value)
                        : value
                            ? new Date(value)
                            : null;

                return (
                    <DatePicker
                        value={dateValue ? dayjs(dateValue) : null}
                        onChange={(d) =>
                            handleCellChange(
                                record.key,
                                header,
                                d ? d.toDate() : ""
                            )
                        }
                        size="small"
                        style={missing ? { border: "1px solid red" } : {}}
                    />
                );
            }

            if (type === "time") {
                let timeString = excelTimeHandler(value);

                return (
                    <TimePicker
                        value={timeString ? dayjs(timeString, "HH:mm") : null}
                        format="HH:mm"
                        onChange={(t) => {
                            handleCellChange(
                                record.key,
                                header,
                                t ? t.format("HH:mm") : ""
                            )
                        }
                        }
                        size="small"
                        minuteStep={15}
                        style={missing ? { border: "1px solid red" } : {}}
                    />
                );
            }
            if (type === "number") {
                return (
                    <InputNumber
                        value={value}
                        onChange={(v) =>
                            handleCellChange(record.key, header, v)
                        }
                        size="small"
                        style={{
                            width: "100%",
                            ...(missing && { border: "1px solid red" }),
                        }}
                    />
                );
            }
            return (
                <Input
                    value={value}
                    onChange={(e) =>
                        handleCellChange(record.key, header, e.target.value)
                    }
                    size="small"
                    style={
                        missing
                            ? {
                                border: "1px solid #ff4d4f",
                                background: "#fff1f0",
                                color: "#a8071a",
                            }
                            : {}
                    }
                />
            );
        },
    }));
};

export const generateStaffMonthlyPayload = (rows, fileType, dateString = "", config, filter) => {
    const payload = {};
    if (fileType === "monthly") {
        payload.month = dateString;
        payload.staff = []
        map(rows, row => {
            const empType = find(get(config, "staff_types"), (item) => get(item, 'name') === row['Staff Type']);
            const roleObj = find(Object.values(get(config, "staff_roles")), (item) => get(item, 'name') === row['Role'])
            const modifiedRow = {
                "name": row['Staff Name'],
                "emp_id": row['Staff ID'],
                "manager": row['Staff Manager'],
                "manager_id": row['Staff Manager ID'],
                "role_id": get(roleObj, "value"),
                "per_hour_salary": row['Salary/PH'],
                "employee_type": get(empType, "value"),
            }
            payload.staff.push(modifiedRow)
        })
    }
    if (fileType === "daily") {
        payload.attendance = [];
        map(rows, row => {
            const shift = find(
                get(filter, "company.company_shifts", []),
                (shift) =>
                    includes(
                        toLower(get(shift, "name", "")),
                        toLower(row["Shift"])
                    )
            );
            const modifiedRow = {
                "date": dayjs(excelSerialToDate(row['Date'])).format('YYYY-MM-DD'),
                "name": row['Name'],
                "emp_id": row["Staff EMP_ID"],
                "in_time": excelTimeHandler(row['In Time']),
                "out_time": excelTimeHandler(row['Out Time']),
                "shift_uuid": get(shift, "id")
            }
            if (row["Shift"]) {
                payload.attendance.push(modifiedRow)
            }
        })
    }
    return payload;
}
